import cn from 'classnames';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { patientAgentMessages, type ThreadApprovalItem } from '../config';
import { useCurrentProposalState } from '../hooks';
import {
  diffSchemaProposal,
  diffTemplateSections,
  parseProposal,
  type MetaFieldChange,
  type SchemaDocumentDiff,
  type TemplateSectionDiff,
} from '../lib';
import { shortID } from '../lib';

import styles from './AgentChat.module.scss';

// The approval gate is generic; the preview body is object-specific. Each
// proposal kind registers its renderer here; unknown kinds fall back to the
// generic structured view.

const metaFieldMessages = {
  name: patientAgentMessages.approvalFieldName,
  tabTitle: patientAgentMessages.approvalFieldTabTitle,
  description: patientAgentMessages.approvalFieldDescription,
  type: patientAgentMessages.approvalFieldType,
  language: patientAgentMessages.approvalFieldLanguage,
} as const;

const MetaChangeRow: FC<{ change: MetaFieldChange }> = ({ change }) => {
  const { formatMessage } = useIntl();
  return (
    <div className={styles.approvalMetaRow}>
      <span className={styles.approvalMetaLabel}>
        {formatMessage(metaFieldMessages[change.field])}
      </span>
      <span className={styles.approvalMetaValue}>
        {change.from ? (
          <del className={styles.approvalRemoved}>{change.from}</del>
        ) : null}
        {change.from && change.to ? (
          <span className={styles.approvalArrow}>→</span>
        ) : null}
        {change.to ? (
          <ins className={styles.approvalAdded}>{change.to}</ins>
        ) : null}
      </span>
    </div>
  );
};

const MetaPlainRow: FC<{
  field: keyof typeof metaFieldMessages;
  value: string;
}> = ({ field, value }) => {
  const { formatMessage } = useIntl();
  return (
    <div className={styles.approvalMetaRow}>
      <span className={styles.approvalMetaLabel}>
        {formatMessage(metaFieldMessages[field])}
      </span>
      <span className={styles.approvalMetaValue}>{value}</span>
    </div>
  );
};

const SectionBadges: FC<{ section: TemplateSectionDiff['section'] }> = ({
  section,
}) => {
  const { formatMessage } = useIntl();
  return (
    <span className={styles.approvalBadges}>
      {section.templateType ? (
        <span className={styles.approvalBadge}>
          {section.templateType === 'strict'
            ? formatMessage(patientAgentMessages.approvalFieldStrict)
            : section.templateType === 'flexible'
              ? formatMessage(patientAgentMessages.approvalFieldFlexible)
              : section.templateType}
        </span>
      ) : null}
      {section.required ? (
        <span className={styles.approvalBadge}>
          {formatMessage(patientAgentMessages.approvalFieldRequired)}
        </span>
      ) : null}
    </span>
  );
};

const SectionChangeLine: FC<{
  change: TemplateSectionDiff['fieldChanges'][number];
}> = ({ change }) => {
  const { formatMessage } = useIntl();
  const label =
    change.field === 'required'
      ? formatMessage(patientAgentMessages.approvalFieldRequired)
      : change.field === 'templateType'
        ? formatMessage(patientAgentMessages.approvalFieldStrict)
        : '';
  const hasFrom = change.from !== undefined;
  const hasTo = change.to !== undefined;
  // Booleans arrive raw from the diff lib; the em-dash marks an absent value.
  const display = (value: string | boolean | undefined): string =>
    value === undefined
      ? formatMessage(patientAgentMessages.approvalValueNone)
      : typeof value === 'boolean'
        ? formatMessage(
            value
              ? patientAgentMessages.approvalValueYes
              : patientAgentMessages.approvalValueNo,
          )
        : value;
  return (
    <div className={styles.approvalSectionChange}>
      {label ? <span className={styles.approvalMetaLabel}>{label}</span> : null}
      <span className={styles.approvalSectionChangeValues}>
        {hasFrom ? (
          <del className={styles.approvalRemoved}>{display(change.from)}</del>
        ) : null}
        {hasFrom && hasTo ? (
          <span className={styles.approvalArrow}>→</span>
        ) : null}
        {hasTo ? (
          <ins className={styles.approvalAdded}>{display(change.to)}</ins>
        ) : null}
      </span>
    </div>
  );
};

const SectionDiffRow: FC<{ row: TemplateSectionDiff }> = ({ row }) => {
  const { formatMessage } = useIntl();
  const statusMessage =
    row.status === 'added'
      ? patientAgentMessages.approvalDiffAdded
      : row.status === 'removed'
        ? patientAgentMessages.approvalDiffRemoved
        : patientAgentMessages.approvalDiffChanged;
  return (
    <div
      className={cn(
        styles.approvalSectionRow,
        row.status === 'added' && styles.approvalSectionAdded,
        row.status === 'removed' && styles.approvalSectionRemoved,
        row.status === 'changed' && styles.approvalSectionChanged,
      )}
    >
      <div className={styles.approvalSectionTitle}>
        <span className={styles.approvalBadge}>
          {formatMessage(statusMessage)}
        </span>
        <strong
          className={
            row.status === 'removed' ? styles.approvalRemoved : undefined
          }
        >
          {row.title}
        </strong>
        {row.status !== 'removed' ? (
          <SectionBadges section={row.section} />
        ) : null}
      </div>
      {row.status === 'removed' ? (
        row.section.instructions ? (
          <del
            className={cn(styles.approvalInstructions, styles.approvalRemoved)}
          >
            {row.section.instructions}
          </del>
        ) : null
      ) : (
        <>
          {row.fieldChanges
            .filter((change) => change.field !== 'instructions')
            .map((change) => (
              <SectionChangeLine key={change.field} change={change} />
            ))}
          {row.section.instructions ? (
            <p className={styles.approvalInstructions}>
              {row.section.instructions}
            </p>
          ) : null}
        </>
      )}
    </div>
  );
};

const DocumentDiffRow: FC<{ row: SchemaDocumentDiff; name?: string }> = ({
  row,
  name,
}) => {
  const { formatMessage } = useIntl();
  const statusMessage =
    row.status === 'added'
      ? patientAgentMessages.approvalDiffAdded
      : row.status === 'removed'
        ? patientAgentMessages.approvalDiffRemoved
        : patientAgentMessages.approvalDiffChanged;
  const inclusionMessage =
    row.inclusion === 'on_demand'
      ? patientAgentMessages.approvalInclusionOnDemand
      : patientAgentMessages.approvalInclusionAlways;
  const previousInclusionMessage =
    row.previousInclusion === 'on_demand'
      ? patientAgentMessages.approvalInclusionOnDemand
      : patientAgentMessages.approvalInclusionAlways;
  return (
    <div
      className={cn(
        styles.approvalSectionRow,
        row.status === 'added' && styles.approvalSectionAdded,
        row.status === 'removed' && styles.approvalSectionRemoved,
        row.status === 'changed' && styles.approvalSectionChanged,
      )}
    >
      <div className={styles.approvalSectionTitle}>
        <span className={styles.approvalBadge}>
          {formatMessage(statusMessage)}
        </span>
        <strong
          className={
            row.status === 'removed' ? styles.approvalRemoved : undefined
          }
        >
          {name ?? shortID(row.seriesID)}
        </strong>
        {row.status !== 'removed' ? (
          <span className={styles.approvalBadge}>
            {formatMessage(inclusionMessage)}
          </span>
        ) : null}
      </div>
      {row.status === 'changed' ? (
        <div className={styles.approvalSectionChange}>
          <span className={styles.approvalSectionChangeValues}>
            <del className={styles.approvalRemoved}>
              {formatMessage(previousInclusionMessage)}
            </del>
            <span className={styles.approvalArrow}>→</span>
            <ins className={styles.approvalAdded}>
              {formatMessage(inclusionMessage)}
            </ins>
          </span>
        </div>
      ) : null}
    </div>
  );
};

const TemplateProposalPreview: FC<{ item: ThreadApprovalItem }> = ({
  item,
}) => {
  const { formatMessage } = useIntl();
  // The hook's effect keys on the parsed object: memoize it so the identity
  // is stable across renders (a fresh parse every render would loop the
  // effect forever, re-firing the state request each round).
  const parsed = useMemo(
    () => parseProposal(item.kind, item.preview),
    [item.kind, item.preview],
  );
  const state = useCurrentProposalState(parsed);
  if (parsed.kind !== 'template') {
    return null;
  }
  const diff = diffTemplateSections(state.template, parsed);

  if (parsed.baseSeriesID && state.loading) {
    return (
      <p className={styles.approvalStatus}>
        {formatMessage(patientAgentMessages.approvalDiffLoading)}
      </p>
    );
  }

  return (
    <div className={styles.approvalDiff}>
      <div className={styles.approvalMeta}>
        {diff.isNew || state.failed ? (
          <>
            {parsed.meta.name ? (
              <MetaPlainRow field="name" value={parsed.meta.name} />
            ) : null}
            {parsed.meta.tabTitle ? (
              <MetaPlainRow field="tabTitle" value={parsed.meta.tabTitle} />
            ) : null}
            {parsed.meta.type ? (
              <MetaPlainRow field="type" value={parsed.meta.type} />
            ) : null}
            {parsed.meta.language ? (
              <MetaPlainRow field="language" value={parsed.meta.language} />
            ) : null}
            {parsed.meta.description ? (
              <MetaPlainRow
                field="description"
                value={parsed.meta.description}
              />
            ) : null}
          </>
        ) : (
          diff.metaChanges.map((change) => (
            <MetaChangeRow key={change.field} change={change} />
          ))
        )}
      </div>
      {diff.sectionDiffs.length ? (
        <>
          <p className={styles.approvalDiffTitle}>
            {formatMessage(patientAgentMessages.approvalFieldSections)}
          </p>
          {diff.sectionDiffs.map((row) => (
            <SectionDiffRow key={`${row.status}_${row.code}`} row={row} />
          ))}
        </>
      ) : null}
      {!diff.isNew && !state.failed && diff.unchangedSections > 0 ? (
        <p className={styles.approvalDiffUnchanged}>
          {formatMessage(patientAgentMessages.approvalDiffUnchanged, {
            count: diff.unchangedSections,
          })}
        </p>
      ) : null}
    </div>
  );
};

const SchemaProposalPreview: FC<{ item: ThreadApprovalItem }> = ({ item }) => {
  const { formatMessage } = useIntl();
  // Same memoization contract as TemplateProposalPreview above.
  const parsed = useMemo(
    () => parseProposal(item.kind, item.preview),
    [item.kind, item.preview],
  );
  const state = useCurrentProposalState(parsed);
  if (parsed.kind !== 'schema') {
    return null;
  }
  const diff = diffSchemaProposal(state.schema, parsed);

  if (parsed.schemaID && state.loading) {
    return (
      <p className={styles.approvalStatus}>
        {formatMessage(patientAgentMessages.approvalDiffLoading)}
      </p>
    );
  }

  const currentDocuments = state.schema?.documents ?? [];
  const documentName = (seriesID: string) =>
    currentDocuments.find(
      (document) => document.template_series_id === seriesID,
    )?.name;
  return (
    <div className={styles.approvalDiff}>
      <div className={styles.approvalMeta}>
        {diff.isNew || state.failed ? (
          <>
            {parsed.meta.name ? (
              <MetaPlainRow field="name" value={parsed.meta.name} />
            ) : null}
            {parsed.meta.description ? (
              <MetaPlainRow
                field="description"
                value={parsed.meta.description}
              />
            ) : null}
          </>
        ) : (
          diff.metaChanges.map((change) => (
            <MetaChangeRow key={change.field} change={change} />
          ))
        )}
      </div>
      {diff.documentDiffs.length ? (
        <>
          <p className={styles.approvalDiffTitle}>
            {formatMessage(patientAgentMessages.approvalFieldDocuments)}
          </p>
          {diff.documentDiffs.map((row) => (
            <DocumentDiffRow
              key={row.seriesID}
              row={row}
              name={documentName(row.seriesID)}
            />
          ))}
        </>
      ) : null}
    </div>
  );
};

/** Fallback for proposal kinds without a dedicated renderer: shallow
 * key-value view of the proposal, never raw JSON. */
const GenericProposalPreview: FC<{ item: ThreadApprovalItem }> = ({ item }) => {
  const entries = Object.entries(item.preview ?? {}).filter(
    ([, value]) => typeof value !== 'object' || value === null,
  );
  return (
    <div className={styles.approvalDiff}>
      <div className={styles.approvalMeta}>
        {entries.map(([key, value]) => (
          <div key={key} className={styles.approvalMetaRow}>
            <span className={styles.approvalMetaLabel}>{key}</span>
            <span className={styles.approvalMetaValue}>{String(value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const proposalPreviewRenderers: Record<
  string,
  FC<{ item: ThreadApprovalItem }>
> = {
  template: TemplateProposalPreview,
  schema: SchemaProposalPreview,
};

export const ProposalPreview: FC<{ item: ThreadApprovalItem }> = ({ item }) => {
  const Renderer =
    proposalPreviewRenderers[item.kind] ?? GenericProposalPreview;
  return <Renderer item={item} />;
};
